/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { TestPaneSeparator } from 'src/luis/projects/testPane/components/common/TestPaneSeparator';
import { EntityInspector, EntityInspectorProps } from 'src/luis/projects/testPane/components/UtteranceInspector/EntityInspector';
import { IntentInspector, IntentInspectorProps } from 'src/luis/projects/testPane/components/UtteranceInspector/IntentInspector';
import { PatternInspector, PatternInspectorProps } from 'src/luis/projects/testPane/components/UtteranceInspector/PatternInspector';
import { SentimentInspector, SentimentInspectorProps } from 'src/luis/projects/testPane/components/UtteranceInspector/SentimentInspector';

type UtteranceInspectorProps = IntentInspectorProps &
    EntityInspectorProps &
    PatternInspectorProps &
    SentimentInspectorProps & {
        /**
         * The utterance text to display as a title.
         */
        utteranceText: string;
    };

const messages = defineMessages({
    userInput: {
        id: 'testPane.utteranceInspector.userInput',
        defaultMessage: 'User input'
    }
});

const sectionTheme = getScopedTheme('UtteranceInspector');
const { styleComponent } = getStylist('UtteranceInspector');

const Root = styleComponent<IStackProps>(Stack)('Root', theme => ({ color: theme.textColor }));

/**
 * Represents the inspector that displays the details
 * for a test utterance.
 *
 * @param props The input properties to pass to the sub
 * inspectors composed in this one.
 */
export const UtteranceInspector = (props: UtteranceInspectorProps) => {
    const { localize } = useLocalization();

    return (
        <Root tokens={{ childrenGap: 10 }}>
            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText lineHeight="20px" fontWeight="bold">
                    {localize(messages.userInput)}
                </LuisText>
                <LuisText lineHeight="16px" styles={{ root: { color: sectionTheme.utteranceTextColor } }}>
                    {props.utteranceText}
                </LuisText>
            </Stack>

            <TestPaneSeparator />

            <IntentInspector {...props} />

            <TestPaneSeparator />

            <EntityInspector {...props} />

            <TestPaneSeparator />

            <PatternInspector {...props} />

            <TestPaneSeparator />

            <SentimentInspector {...props} />
        </Root>
    );
};
